/**
 * The answer engine: text so far in, ordered atoms out, with nothing that could still change.
 *
 * `parseRaw(text)` is what micromark makes of the text as it stands. It is not stable while text
 * is arriving: an unfinished `:tag[On ho` is an empty chip, `**bo` is plain text until the star
 * closes, a header line is a paragraph until the divider under it comes. `parseHeld(text, final,
 * prev)` is the same atoms with only those kept that survive a set of probes, which are the text
 * with a plausible continuation added (a closer, one more letter, a table divider). It has no
 * rule per syntax, so a directive with a name nobody has met yet needs nothing here: this file
 * does not know the widget registry exists.
 *
 * Known limit: on random punctuation (not on answers) the hold-back still changes something it
 * already showed in about 8% of strings. The reveal notices and redraws quietly (see reveal.ts).
 *
 * Ported from the Streaming Answer Lab's `mm-engine.mjs`; see docs/architecture/decision-log.md.
 */
import type { Atom, Attrs, Cell, Frame } from "./atoms";
import { type Root, tree } from "./markdown";
import { fenceOneLine } from "./one-line";

type Node = Root["children"][number];

/**
 * A page of this app an answer may link to by its path: a case (`/cases/{id}`) or a playbook's
 * Activity (`/activity/{id}`), which the chat's `start_cases` answer names. Nothing else relative
 * is a link.
 */
const IN_APP = /^\/(cases|activity)\/[a-z]+_[0-9a-z]{26}$/;

/** Whether a link's address is a page of this app, which opens in place rather than in a new tab. */
export const isInAppHref = (href: string): boolean => IN_APP.test(href);

/**
 * Only http and https addresses are links, and the app's own case and Activity pages by their
 * path; anything else is shown as the text it labels.
 */
function safeHref(href: string): string | null {
  if (isInAppHref(href)) return href;
  try {
    const url = new URL(href);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

const childrenOf = (node: Node): readonly Node[] => ("children" in node ? node.children : []);

function textOf(node: Node): string {
  if ("value" in node && typeof node.value === "string") return node.value;
  return childrenOf(node).map(textOf).join("");
}

function cleanAttrs(
  attributes: Record<string, string | null | undefined> | null | undefined,
): Attrs {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(attributes ?? {})) {
    if (value !== null && value !== undefined) out[key] = value;
  }
  return out;
}

const startOf = (node: Node) => node.position?.start.offset ?? 0;
const endOf = (node: Node) => node.position?.end.offset ?? 0;

type Computed = "cost" | "top" | "sig";
type Init<T> = T extends Atom ? Omit<T, Computed> : never;
type AtomInit = Init<Atom>;

const COST: Partial<Record<Atom["kind"], number>> = {
  word: 1,
  open: 1.5,
  row: 3,
  line: 2.5,
  hr: 1,
};

function pathKey(path: readonly Frame[]): unknown[] {
  return path.map((frame) => {
    switch (frame.kind) {
      case "container":
        return [frame.id, frame.name, frame.attrs, frame.label, frame.leaf];
      case "inline":
        return [frame.id, frame.tag, frame.href ?? null];
      case "block":
        return [frame.id, frame.tag];
      case "table":
        return [frame.id, "table"];
    }
  });
}

function extras(atom: AtomInit): unknown {
  switch (atom.kind) {
    case "word":
    case "code":
    case "line":
      return atom.text;
    case "cite":
      return atom.nums;
    case "chip":
      return [atom.name, atom.text, atom.attrs];
    case "row":
      return [atom.head, atom.cells.map((cell) => [cell.text, cell.align])];
    case "open":
    case "hr":
      return null;
  }
}

/**
 * `[1]`, `[1, 2]`: a citation. Not when a word runs straight into it, so `array[0]` stays a word.
 */
const CITE = /((?<![\p{L}\p{N}])\[\d+(?:\s*,\s*\d+)*\])/u;
const isCite = (part: string) => part.startsWith("[") && CITE.test(part);

/** mdast to atoms. `topBase` lets a re-parse of the last block keep the ids of the whole parse. */
function atomize(root: Root, source: string, topBase: number): Atom[] {
  const atoms: Atom[] = [];
  let top = 0;

  const finish = (atom: AtomInit): Atom => ({
    ...atom,
    cost: COST[atom.kind] ?? 1.4,
    top,
    sig: JSON.stringify([atom.kind, atom.lead, extras(atom), pathKey(atom.path)]),
  });

  function inlineInto(
    out: Atom[],
    nodes: readonly Node[],
    basePath: readonly Frame[],
    blockId: string,
  ): void {
    const local = { counter: 0, lead: false, emitted: 0 };
    // `lead` is whether a space comes before an atom; the first atom of a run never has one.
    const emit = (make: (id: string, lead: boolean) => AtomInit) => {
      const lead = local.lead && local.emitted > 0;
      local.lead = false;
      local.emitted++;
      out.push(finish(make(`${blockId}:${String(local.counter++)}`, lead)));
    };

    function text(value: string, from: number, marks: readonly Frame[]): void {
      let position = 0;
      for (const part of value.split(CITE)) {
        if (isCite(part)) {
          position += part.length;
          const end = from + position;
          emit((id, lead) => ({
            id,
            lead,
            kind: "cite",
            nums: part
              .slice(1, -1)
              .split(",")
              .map((num) => Number(num.trim())),
            path: basePath.concat(marks),
            e: end,
          }));
          continue;
        }
        for (const match of part.matchAll(/(\s+)|(\S+)/g)) {
          position += match[0].length;
          if (match[1]) {
            local.lead = true;
            continue;
          }
          const end = from + position;
          const word = match[0];
          emit((id, lead) => ({
            id,
            lead,
            kind: "word",
            text: word,
            path: basePath.concat(marks),
            e: end,
          }));
        }
      }
    }

    function walk(list: readonly Node[], marks: readonly Frame[], idPrefix: string): void {
      list.forEach((node, index) => {
        const nodeId = `${idPrefix}${String(index)}`;
        switch (node.type) {
          case "text":
            text(node.value, startOf(node), marks);
            break;
          case "emphasis":
            walk(
              node.children,
              marks.concat({ id: `${blockId}:e${nodeId}`, kind: "inline", tag: "em" }),
              `${nodeId}.`,
            );
            break;
          case "strong":
            walk(
              node.children,
              marks.concat({ id: `${blockId}:s${nodeId}`, kind: "inline", tag: "strong" }),
              `${nodeId}.`,
            );
            break;
          case "link": {
            const href = safeHref(node.url);
            walk(
              node.children,
              href
                ? marks.concat({ id: `${blockId}:a${nodeId}`, kind: "inline", tag: "a", href })
                : marks,
              `${nodeId}.`,
            );
            break;
          }
          case "inlineCode":
            emit((id, lead) => ({
              id,
              lead,
              kind: "code",
              text: node.value,
              path: basePath.concat(marks),
              e: endOf(node),
            }));
            break;
          case "textDirective": {
            // A colon in prose is not a directive: `10:30` and `Re:INV-9` have a name after the
            // colon too, so a directive with neither a label nor attributes stays the text it was.
            const written = source.slice(startOf(node), endOf(node));
            if (!written.startsWith(`:${node.name}`) || !/[[{]/.test(written)) {
              text(written, startOf(node), marks);
              break;
            }
            emit((id, lead) => ({
              id,
              lead,
              kind: "chip",
              name: node.name,
              text: textOf(node),
              attrs: cleanAttrs(node.attributes),
              path: basePath.concat(marks),
              e: endOf(node),
            }));
            break;
          }
          case "break":
            local.lead = true;
            break;
          case "image":
            text(node.alt ?? "", startOf(node), marks);
            break;
          case "html":
          case "footnoteReference":
            // Never rendered.
            break;
          default:
            walk(childrenOf(node), marks, `${nodeId}.`);
        }
      });
    }

    walk(nodes, [], "");
  }

  function blocks(
    children: readonly Node[],
    path: readonly Frame[],
    prefix: string,
    offset: number,
    isTop: boolean,
  ): void {
    children.forEach((node, index) => {
      const id = `${prefix}${String(index + offset)}`;
      if (isTop) top = index + offset;
      switch (node.type) {
        case "paragraph":
          inlineInto(atoms, node.children, path.concat({ id, kind: "block", tag: "p" }), id);
          break;
        case "heading":
          inlineInto(
            atoms,
            node.children,
            path.concat({
              id,
              kind: "block",
              tag: node.depth === 1 ? "h3" : node.depth === 2 ? "h4" : "h5",
            }),
            id,
          );
          break;
        case "list": {
          const listFrame: Frame = { id, kind: "block", tag: node.ordered ? "ol" : "ul" };
          node.children.forEach((item, itemIndex) => {
            const itemId = `${id}.${String(itemIndex)}`;
            const itemPath = path.concat(listFrame, { id: itemId, kind: "block", tag: "li" });
            item.children.forEach((child, childIndex) => {
              // The first paragraph of an item is never wrapped, so a list turning "loose" later
              // changes nothing that is already shown.
              if (child.type === "paragraph" && childIndex === 0) {
                inlineInto(atoms, child.children, itemPath, itemId);
              } else {
                blocks([child], itemPath, `${itemId}.`, childIndex, false);
              }
            });
          });
          break;
        }
        case "blockquote":
          blocks(
            node.children,
            path.concat({ id, kind: "block", tag: "blockquote" }),
            `${id}.`,
            0,
            false,
          );
          break;
        case "code": {
          const frames = path.concat({ id, kind: "block", tag: "pre" });
          const e = endOf(node);
          atoms.push(finish({ id: `${id}:o`, kind: "open", lead: false, path: frames, e }));
          node.value.split("\n").forEach((line, lineIndex) => {
            atoms.push(
              finish({
                id: `${id}:${String(lineIndex)}`,
                kind: "line",
                lead: false,
                text: line,
                path: frames,
                e,
              }),
            );
          });
          break;
        }
        case "thematicBreak":
          atoms.push(finish({ id: `${id}:hr`, kind: "hr", lead: false, path, e: endOf(node) }));
          break;
        case "table": {
          const tableFrame: Frame = { id, kind: "table" };
          const columns = node.children[0]?.children.length ?? 0;
          node.children.forEach((row, rowIndex) => {
            const head = rowIndex === 0;
            const cells: Cell[] = row.children.map((cell, cellIndex) => {
              const cellId = `${id}:r${String(rowIndex)}c${String(cellIndex)}`;
              const items: Atom[] = [];
              inlineInto(items, cell.children, [], cellId);
              const align = node.align?.[cellIndex];
              return {
                id: cellId,
                text: source.slice(startOf(cell), endOf(cell)).trim(),
                align: align === "right" ? "r" : align === "center" ? "c" : "",
                items,
              };
            });
            while (cells.length < columns) {
              cells.push({
                id: `${id}:r${String(rowIndex)}c${String(cells.length)}`,
                text: "",
                align: "",
                items: [],
              });
            }
            atoms.push(
              finish({
                id: `${id}:r${String(rowIndex)}`,
                kind: "row",
                lead: false,
                head,
                cells,
                e: endOf(row),
                path: path.concat(tableFrame, {
                  id: id + (head ? "h" : "b"),
                  kind: "block",
                  tag: head ? "thead" : "tbody",
                }),
              }),
            );
          });
          break;
        }
        case "containerDirective":
        case "leafDirective": {
          // A leaf directive's label is its children; a container's is a first paragraph marked as one.
          let kids: readonly Node[] = node.children;
          let label = "";
          const first = kids[0];
          if (node.type === "leafDirective") {
            label = textOf(node);
            kids = [];
          } else if (first?.type === "paragraph" && first.data?.directiveLabel) {
            label = textOf(first);
            kids = kids.slice(1);
          }
          const frames = path.concat({
            id,
            kind: "container",
            name: node.name,
            attrs: cleanAttrs(node.attributes),
            label,
            leaf: node.type === "leafDirective",
          });
          atoms.push(
            finish({ id: `${id}:o`, kind: "open", lead: false, path: frames, e: endOf(node) }),
          );
          blocks(kids, frames, `${id}.`, 0, false);
          break;
        }
        case "html":
        case "definition":
        case "yaml":
          // Never rendered.
          break;
        default:
          blocks(childrenOf(node), path, `${id}.`, 0, false);
      }
    });
  }

  blocks(root.children, [], "b", topBase, true);
  return atoms;
}

/** What micromark makes of the text as it stands, nothing held back. Unstable while text arrives. */
export function parseRaw(source: string): Atom[] {
  const text = fenceOneLine(source);
  return atomize(tree(text), text, 0);
}

/* ---- hold-back by probing ---- */

/** Plausible continuations: each one is a way the text might go on that changes how the end reads. */
const PROBES: readonly string[] = [
  // the word, the line or the paragraph goes on
  "x",
  " x",
  "\nx",
  "\n\nx",
  // a closer, which CommonMark only accepts after a non-space
  "x**",
  "x*",
  "x_",
  "x***",
  "x`",
  "x``",
  "**",
  "`",
  // a link or a directive finishing
  "x]",
  "x)",
  "x}",
  "x](https://x.y)",
  "]",
  ")",
  "}",
  "](https://x.y)",
  "(https://x.y)",
  "]{a=b}",
  "{a=b}",
  // each state inside {attributes}
  "=x}",
  'x"}',
  "a=b}",
  '"x"}',
  '"}\n',
  // a list, a fence or a container opening or closing
  ". x",
  "\n- x",
  "```\n",
  ":::\n",
  // nested parentheses, and a table divider arriving in pieces
  "))",
  "x))",
  "|",
  "-|",
  "--|",
  "---|",
  "--",
  "---",
];

const cellCount = (line: string) =>
  line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").length;

function tableProbes(text: string): string[] {
  const out: string[] = [];
  const last = text.replace(/\s+$/, "").split("\n").pop() ?? "";
  // a header line with no divider yet
  if (last.includes("|")) {
    out.push(
      `${text.endsWith("\n") ? "" : "\n"}|${"---|".repeat(cellCount(last))}\n`,
      " x |\n|---|\n",
    );
  }
  // a divider line arriving in pieces: the header is the line before, and it fixes how many cells the divider needs
  const lines = text.split("\n");
  const previous = lines[lines.length - 2] ?? "";
  if (previous.includes("|")) {
    for (let n = 1; n <= cellCount(previous) + 1; n++) {
      out.push("---|".repeat(n), `|${"---|".repeat(n)}`, `|${"---|".repeat(n)}\n`);
    }
  }
  return out;
}

/** How many atoms at the start of both lists are the same. */
function common(a: readonly Atom[], b: readonly Atom[]): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i]?.sig === b[i]?.sig) i++;
  return i;
}

/** A hold-back never stalls more than this many characters for one unclosed marker. */
export const MAX_HOLD = 360;

type Container = Extract<Node, { type: "containerDirective" }>;

/** The children of a container, without the label paragraph that `:::name[label]` makes. */
function bodyOf(node: Container): readonly Node[] {
  const first = node.children[0];
  return first?.type === "paragraph" && first.data?.directiveLabel
    ? node.children.slice(1)
    : node.children;
}

/**
 * Whether a container directive is still open at the end of the text: its last line is not a
 * closing fence (at least as many colons as the one that opened it).
 */
function isOpen(node: Container, text: string): boolean {
  const source = text.slice(startOf(node), endOf(node));
  if (!source.includes("\n")) return true;
  const opened = /^:+/.exec(source)?.[0].length ?? 0;
  const closing = /^\s*(:+)\s*$/.exec(source.slice(source.lastIndexOf("\n") + 1));
  return !closing || (closing[1]?.length ?? 0) < opened;
}

/**
 * Where the block that can still change starts. Normally that is the last top-level block. When
 * it is a container directive that has not been closed, only its last child can change, so the
 * probes need not read the rest: a card holding a whole answer would otherwise be parsed in full
 * for every probe on every chunk (140ms a chunk at 6KB; this makes it the cost of the last
 * paragraph). The probes re-parse the opening lines of the containers around it, and as many
 * placeholder paragraphs as the children before it, so that ids come out as in the full parse.
 */
function openTail(
  root: Root,
  text: string,
  descend: boolean,
): { stub: string; from: number; id: string } {
  const lastIndex = root.children.length - 1;
  let node: Node | undefined = root.children[lastIndex];
  let id = `b${String(lastIndex)}`;
  let stub = "";
  if (!descend) return { stub, from: node ? startOf(node) : 0, id };
  while (node?.type === "containerDirective" && isOpen(node, text)) {
    const body = bodyOf(node);
    const last = body.at(-1);
    const first = body[0];
    if (!last || !first) break;
    stub += text.slice(startOf(node), startOf(first)) + "x\n\n".repeat(body.length - 1);
    id += `.${String(body.length - 1)}`;
    node = last;
  }
  return { stub, from: node ? startOf(node) : 0, id };
}

/** Whether an atom belongs to the block with this id (and not to a sibling whose id starts the same). */
const inside = (atom: Atom, id: string) =>
  atom.id.startsWith(id) && (atom.id[id.length] === ":" || atom.id[id.length] === ".");

/**
 * The atoms of the text so far that cannot change when more text arrives. With `final` set, all of
 * them. `prev` is what this returned last time: anything already committed stays, as long as the
 * plain parse still agrees with it. `descend: false` probes the whole last top-level block instead
 * of only the end of an open container: slower, and the tests hold the two to the same answer.
 */
export function parseHeld(
  source: string,
  final: boolean,
  prev?: readonly Atom[],
  { descend = true }: { descend?: boolean } = {},
): Atom[] {
  // A container directive on one line gets its lines before anything reads the text (one-line.ts);
  // from here on the text is the one the rest of this function works on, offsets and all.
  const text = fenceOneLine(source);
  const root = tree(text);
  const all = atomize(root, text, 0);
  if (final || root.children.length === 0) return all;

  // Only the last block can still change (setext headings and definitions are off), so what comes
  // before it is frozen and the probes re-parse just the end.
  const lastIndex = root.children.length - 1;
  const { stub, from, id } = openTail(root, text, descend);
  const tailAtoms = all.filter((atom) =>
    descend && stub !== "" ? inside(atom, id) : atom.top === lastIndex,
  );
  const frozen = all.slice(0, all.length - tailAtoms.length);
  const tail = text.slice(from);

  let keep = tailAtoms.length;
  for (const probe of PROBES.concat(tableProbes(tail))) {
    const source = stub + tail + probe;
    const probed = atomize(tree(source), source, lastIndex).filter((atom) =>
      stub !== "" ? inside(atom, id) : atom.top === lastIndex,
    );
    keep = Math.min(keep, common(tailAtoms, probed));
    if (keep === 0) break;
  }
  if (keep < tailAtoms.length) {
    const end = keep > 0 ? (tailAtoms[keep - 1]?.e ?? from) : from;
    // Never stall a whole paragraph for one unclosed marker.
    if (text.length - end > MAX_HOLD) keep = tailAtoms.length;
  }

  let out = frozen.concat(tailAtoms.slice(0, keep));
  if (prev && prev.length > 0) {
    const agreed = common(prev, all);
    if (agreed > out.length) out = all.slice(0, agreed);
  }
  return out;
}
